import { Component, effect, input, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MdbScrollbarModule } from 'mdb-angular-ui-kit/scrollbar';
import { MdbSortChange, MdbTableModule } from 'mdb-angular-ui-kit/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CurrencyPipe, DatePipe, DecimalPipe, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MdbButtonComponent } from '../mdb-button/mdb-button.component';
import {
  SelectorModel,
  TableButtonModel,
  TableColumnModel,
  TableLoadingModel
} from '../../../models/table-column.model';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { StarRatingComponent } from '../star-rating/star-rating.component';
import { RequestOptions } from '../../../models/request-options.model';
import { PaginationMeta } from '../../../models/pagination-meta.model';
import { PaginationComponent } from '../pagination/pagination.component';
import { UrlParamModel } from '../../../models/url-param.model';
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';
import { environment } from '../../../../environments/environment';
import { FilterFormControlComponent } from '../filter-form-control/filter-form-control.component';
import { UtilsHelper } from '../../../helpers/utils-helper';
import { FormGroupHelper } from '../../../helpers/form-group-helper';

@Component({
  selector: 'app-table',
  imports: [
    ReactiveFormsModule,
    MdbScrollbarModule,
    MdbTableModule,
    TranslateModule,
    RouterLink,
    MdbButtonComponent,
    MdbCheckboxModule,
    DatePipe,
    NgOptimizedImage,
    MdbFormsModule,
    MdbSelectModule,
    StarRatingComponent,
    CurrencyPipe,
    PaginationComponent,
    MdbTooltipModule,
    DecimalPipe,
    FilterFormControlComponent
  ],
  templateUrl: './table.component.html',
  standalone: true,
  styleUrl: './table.component.scss'
})
export class TableComponent {
  columns = input.required<TableColumnModel[]>();
  data = input.required<any[]>();
  filterFormGroup = input<FormGroup>(this.formBuilder.group({}));
  selectable = input(false);
  allowMultiSelect = input(true);
  loading = input<TableLoadingModel>({ loading: false });
  hasActiveFilters = input<boolean>();
  standardUrlParams = input<UrlParamModel[]>([])
  // pagination
  pagination = input<PaginationMeta | null>();
  deleteLoadingIds = input<number[]>([]);

  selectionChange = output<any[]>();
  doFilterRequest = output<RequestOptions | undefined>();
  urlParamsChange = output<UrlParamModel[] | undefined>();

  selections = signal<any[]>([]);
  hideTableBody: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService
  ) {
    effect(() => {
      this.hideTableBody =
        this.loading().loading &&
        !this.loading().lazyLoad &&
        // DO NOT SIMPLIFY!
        (this.loading().hasFilters ||
          this.hasActiveFilters() ||
          this.loading().isPaginationChange === true);
    });

    effect(() => {
      this.selectionChange.emit(this.selections());
    });

    // if data changes, update the current selections
    effect(() => {
      if(!this.data())
        return
      this.selections.update((currentSelections: any) => {
        return currentSelections.map((s: any) => {
          return this.data().find(d => d.id === s.id) ?? s
        })
      })
    });

    effect(() => {
      if (Object.keys(this.filterFormGroup().controls).length > 0) {
        this.filterFormGroup().valueChanges.subscribe(() => {
          let urlParams = this.getFilterUrlParams();
          this.urlParamsChange.emit(urlParams.length > 0 ? urlParams : undefined);
          this.doFilterRequest.emit({
            urlParams: urlParams.length > 0 ? urlParams : undefined,
            resetPagination: true
          });
        });
      }
    });
  }

  getControlName(c: AbstractControl): string | null {
    return FormGroupHelper.getControlName(c);
  }

  getArrayData(selector: string, data: any) {
    let splits = selector.split('.');
    let selectorResult = data;

    for (const sp of splits) {
      // If selectorResult is null, immediately return the placeholder
      selectorResult = selectorResult[sp];
      if (!selectorResult)
        return [];
    }

    return selectorResult;
  }

  getButtonLink(button: TableButtonModel, data: any) {
    if(button.isObjectLink)
      return (button.objectBaseLink ? (this.getLink(button.objectBaseLink,data) + '/') : '') + this.getData(null, data, true,);

    return button.routerLink ?? (button.customLink ? this.getLink(button.customLink, data) : undefined)
  }

  getLink(link: string, data: any) {
    // Regex to match parts inside curly braces, e.g., {createUser.employee.id}
    const regex = /\{([^}]+)\}/g;
    let match;
    const matches = [];
    // Collect all matches (placeholders) in the resourceLink
    while ((match = regex.exec(link)) !== null) {
      matches.push(match); // Store each match (including the full string like {createUser.employee.id})
    }
    // Replace each placeholder with the actual value from data
    matches.forEach((match) => {
      const selectorString = match[1]; // Extract the selector inside the braces
      const selectorParts: SelectorModel[] = [{ selector: selectorString }]; // Create a SelectorModel from it
      // Use getData to extract the value using the selector
      const value = this.getData(selectorParts, data);
      // Replace the placeholder in the resourceLink with the actual value
      link = link.replace(match[0], value);
    });
    return link;
  }

  select(data: any) {
    if (!this.allowMultiSelect()) {
      this.unselectAll();
      this.selections.set([data]);
    }
    else {
      this.selections.update((selections) => {
        return [...selections, data];
      });
    }
  }

  selectAll() {
    this.selections.set(this.data());
  }

  unselect(data: any) {
    this.selections.update((selections) => {
      return selections.filter((s) => s.id !== data.id);
    });
  }

  unselectAll() {
    if (!this.isAllSelected()) return;
    this.selections.set([]);
  }

  isSelected(data: any) {
    return this.selections().find((s) => s.id === data.id) != null;
  }

  isAllSelected() {
    let x = true;
    for (const d of this.data()) {
      if (!this.selections().find((s) => s.id == d.id)) {
        x = false;
        break;
      }
    }
    return x;
  }

  getFilterUrlParams() {
    let params: UrlParamModel[] = [...this.standardUrlParams()] // new array object, otherwise standardUrlParams would also change
    Object.keys(this.filterFormGroup().controls).forEach((key, i) => {
      let value = this.filterFormGroup().get(key)?.value; // Access the control value
      if (value) {
        let column = this.columns().find(
          (c) =>
            c.filterFormControl &&
            this.getControlName(c.filterFormControl) == key
        )
        let paramKey = column?.filterUrlParam
        params.push({ key: `${paramKey}`, value: value, isDate: column?.isDate })
      }
    });

    return params;
  }

  paginationPrevious() {
    let urlParams = this.getFilterUrlParams();
    this.doFilterRequest.emit({
      urlParams: urlParams.length > 0 ? urlParams : undefined,
      pagination: { previous: true }
    });
  }

  paginationNext() {
    let urlParams = this.getFilterUrlParams();
    this.doFilterRequest.emit({
      urlParams: urlParams.length > 0 ? urlParams : undefined,
      pagination: { next: true }
    });
  }

  sortColumn(event: MdbSortChange) {
    let urlParams = this.getFilterUrlParams()

    let tableColumn = this.columns().find(d => {return d.name?.toUpperCase() === event.name.toUpperCase()});
    if(tableColumn?.disableSort)
      return;

    if(!tableColumn?.selector || tableColumn?.selector.length > 1)
      return;

    let sortKey = tableColumn!.selector![0].selector
    let sortParams = [
      {
        key: event.direction == 'asc' ? 'order-by-asc' : 'order-by-desc',
        value: sortKey
        // value: FormatHelper.toKebabCase(sortKey)
      }
    ];
    // let sortParams = [{key: event.direction == 'asc' ? 'order-by-asc' : 'order-by-desc', value: FormatHelper.toKebabCase(event.name.toLowerCase())}]
    let mergedParams = [...urlParams, ...sortParams]
    this.urlParamsChange.emit(mergedParams.length > 0 ? mergedParams : undefined)
    this.doFilterRequest.emit({
      urlParams: mergedParams.length > 0 ? mergedParams : undefined,
      resetPagination: true
    })
  }

  btnLoading(btn: TableButtonModel, id: number) {
    return (
      btn.loading ||
      (btn.isDelete && this.deleteLoadingIds().indexOf(id) !== -1)
    );
  }

  getData(selector: SelectorModel[] | null | undefined,
          data: any,
          selectorIsId: boolean = false,
          optionalSelector?: SelectorModel[] | null) {
    return UtilsHelper.getDataOfSelector(selector, data, selectorIsId, this.translate, optionalSelector)
  }

  protected readonly Object = Object;
  protected readonly environment = environment;
}
